import { GripVerticalIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Loading shapes for the screens.
 *
 * Each one mirrors the layout it stands in for — same bar height, same grid,
 * same row rhythm — so the page does not jump when the data lands. A generic
 * spinner is cheaper to write and worse to look at: it tells you nothing about
 * what is coming, and every arrival is a relayout.
 *
 * Deliberately NOT animated per element with staggered delays. These are
 * visible for a few hundred milliseconds; anything cleverer than a single
 * shared pulse reads as a second thing loading.
 */

/**
 * One line of text.
 *
 * The bar is deliberately shorter than the line it stands in for — a block the
 * full line-height reads as a filled field rather than as words — so it is
 * centred inside a box of the REAL line height instead. That is what keeps the
 * geometry honest: the type ramp is 18px body / 22px heading, and a stack of
 * bare `h-3` bars silently loses several pixels per line, which is where the
 * jump on arrival comes from.
 */
function TextLine({
  width,
  line = 'h-[18px]',
  bar = 'h-2.5',
  className,
}: {
  width: string
  /** The line box this text occupies. 18px body, 22px heading. */
  line?: string
  bar?: string
  className?: string
}) {
  return (
    <div className={cn('flex items-center', line, className)}>
      <Skeleton className={cn(bar, width)} />
    </div>
  )
}

/**
 * Matches `ViewBar` — 92px normally, 76px + a 36px tab strip when it carries
 * tabs.
 *
 * One flag switches both the tabs and the actions because they always travel
 * together: the QR code's bar is the only one with tabs, and it is also the only
 * one whose actions are a short-link field plus an overflow button rather than a
 * single button.
 */
export function ViewBarSkeleton({ tabs = false }: { tabs?: boolean }) {
  return (
    <div className="border-border border-b px-6">
      <div
        className={cn(
          'flex items-stretch gap-4',
          tabs ? 'min-h-[76px] pt-4 pb-2' : 'min-h-[92px] py-4',
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          {/* Hidden below `sm`, exactly as the real tile is. */}
          <Skeleton className="hidden size-11 shrink-0 rounded-[var(--radius-tile)] sm:block" />
          <div className="flex min-w-0 flex-col gap-1">
            <TextLine width="w-32" />
            <TextLine width="w-48" line="h-[22px]" bar="h-3" />
          </div>
        </div>
        {/* Bottom-aligned, like the real actions. */}
        <div className="flex shrink-0 items-end gap-2">
          {tabs ? (
            <>
              <Skeleton className="h-8 w-[180px] rounded-lg" />
              <Skeleton className="size-8 rounded-lg" />
            </>
          ) : (
            <Skeleton className="h-8 w-28 rounded-lg" />
          )}
        </div>
      </div>

      {/* The tab strip is 36px tall and its triggers carry px-3 — a bare row of
          bars here left the bar 16px short and moved the whole screen down. */}
      {tabs && (
        <div className="flex h-9 items-center gap-1">
          {['w-[76px]', 'w-[62px]'].map((width) => (
            <div key={width} className="px-3">
              <Skeleton className={cn('h-3', width)} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** The card surface every tile sits on — see `PanelCard` and `QrCodeCard`. */
function CardSurface({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'border-card-border bg-card flex shrink-0 flex-col rounded-[var(--radius-card)]',
        'border-[length:var(--radius-card-border)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** One QR code / group card — same 2-row internal split as the real card. */
function CardSkeleton() {
  return (
    <CardSurface className="p-[var(--radius-card-inset)]">
      <div className="flex items-center gap-3.5">
        <Skeleton className="size-16 shrink-0 rounded-[var(--radius-card-inner)]" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <TextLine width="w-3/4" line="h-[22px]" bar="h-3" />
          <TextLine width="w-1/2" />
        </div>
      </div>
      <div className="border-card-border mt-3 flex items-center gap-2 border-t pt-3">
        <TextLine width="w-24" />
        <TextLine width="w-10" className="ml-auto" />
      </div>
    </CardSurface>
  )
}

/**
 * The groups + codes browser.
 *
 * The track is the real one — `auto-fill` over a 288px minimum — not a
 * breakpoint grid. Column count follows the width the cards actually have, so
 * the placeholders sit where the cards will rather than reflowing under them
 * the moment they land; at a sidebar-narrowed 1440px that is the difference
 * between three columns and four.
 *
 * Card counts are a guess at a typical screenful, not a promise. Rendering one
 * placeholder per real row is impossible — the count is exactly what has not
 * loaded yet.
 */
export function BrowserSkeleton({
  groups = 2,
  codes = 6,
}: {
  groups?: number
  codes?: number
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-5">
      <section className="flex flex-col gap-3">
        <header className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <TextLine width="w-20" line="h-[22px]" bar="h-3" />
            <TextLine width="w-64" />
          </div>
          {/* Only the groups section carries a button. */}
          <Skeleton className="h-8 w-28 rounded-md" />
        </header>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(288px,1fr))] gap-4">
          {Array.from({ length: groups }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <TextLine width="w-24" line="h-[22px]" bar="h-3" />
            <TextLine width="w-72" />
          </div>
        </header>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(288px,1fr))] gap-4">
          {Array.from({ length: codes }, (_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </section>
    </div>
  )
}

/** `SectionCard`'s chrome — header on the wash, outlined body rectangle. */
function PanelCardSkeleton({
  titleWidth,
  children,
}: {
  titleWidth: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-card/40 flex flex-col rounded-[var(--radius-card)]">
      <div className="flex min-h-10 items-center px-4 py-2.5">
        <TextLine width={titleWidth} line="h-[22px]" bar="h-3" />
      </div>
      <div className="border-border mx-4 mb-3 flex min-h-0 flex-1 flex-col rounded-lg border p-2">
        {children}
      </div>
    </div>
  )
}

/**
 * One `StatTile`: label, the number at 24px, then the delta / sparkline row.
 *
 * The third row is drawn only on the first tile, matching the real pane —
 * "Lifetime scans" carries no comparison. The grid stretches both to the taller
 * one either way, so the tiles still come out level.
 */
function StatTileSkeleton({ delta = false }: { delta?: boolean }) {
  return (
    <div className="bg-card/40 flex shrink-0 flex-col gap-1.5 rounded-[var(--radius-card)] p-3.5">
      <TextLine width="w-20" />
      <div className="flex h-6 items-center">
        <Skeleton className="h-5 w-12" />
      </div>
      {delta && (
        <div className="flex h-5 items-end justify-between gap-2">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-3.5 w-16" />
        </div>
      )}
    </div>
  )
}

/** The stat tiles / chart / locations stack in the control panel's left pane. */
export function ScanSummarySkeleton() {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <StatTileSkeleton delta />
        <StatTileSkeleton />
      </div>

      <PanelCardSkeleton titleWidth="w-24">
        <Skeleton className="h-[132px] w-full rounded-lg" />
      </PanelCardSkeleton>

      <PanelCardSkeleton titleWidth="w-24">
        {/* Descending bars: `TopPlaces` is sorted, so a flat stack would be the
            one shape the data cannot arrive in. */}
        <div className="flex flex-col gap-2.5">
          {['w-[86%]', 'w-[61%]', 'w-[43%]', 'w-[28%]'].map((width, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex h-[18px] items-center gap-2">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="ml-auto h-2.5 w-8" />
              </div>
              <div className="bg-muted h-1.5 w-full overflow-hidden rounded-r-[4px]">
                <Skeleton className={cn('h-full rounded-r-[4px]', width)} />
              </div>
            </div>
          ))}
        </div>
      </PanelCardSkeleton>
    </>
  )
}

/** A list of rows — members, and anything else tabular. */
export function RowsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="ml-auto h-8 w-28 rounded-lg" />
      </div>
      <div className="border-card-border divide-border divide-y rounded-xl border">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
            <Skeleton className="h-5 w-16 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Stacked panels — billing, and the schedules detail pane. */
export function PanelsSkeleton({ panels = 2 }: { panels?: number }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
      {Array.from({ length: panels }, (_, i) => (
        <div
          key={i}
          className="border-card-border bg-card flex flex-col gap-4 rounded-xl border p-4"
        >
          <Skeleton className="h-4 w-32" />
          <div className="flex items-start gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/** `QrSummaryPanel` — the code, its identity, and the three actions under it. */
function QrSummarySkeleton() {
  return (
    <div className="flex shrink-0 flex-col gap-5">
      <div className="flex items-center gap-3.5">
        <div className="relative shrink-0">
          {/* A white plate, not a grey block: the real preview is always on one,
              because a QR needs dark modules on a light field to scan. */}
          <div className="size-20 rounded-xl bg-white p-1.5 ring-1 ring-black/10 ring-inset">
            <Skeleton className="size-full rounded-md bg-black/15" />
          </div>
          {/* The identity chip straddling the corner. */}
          <Skeleton className="absolute -right-1.5 -bottom-1.5 size-6 rounded-[7px]" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <TextLine width="w-40" line="h-[22px]" bar="h-3" />
          <TextLine width="w-24" />
          <TextLine width="w-36" />
        </div>
      </div>

      {/* Same wrap rule as the real row, so a narrow pane breaks it the same way. */}
      <div className="flex flex-wrap gap-2 [&>*]:flex-1">
        <Skeleton className="h-8 min-w-[84px] rounded-md" />
        <Skeleton className="h-8 min-w-[100px] rounded-md" />
        <Skeleton className="h-8 min-w-[116px] rounded-md" />
      </div>
    </div>
  )
}

/** One destination tile in `DynamicActionGrid` — icon, name, target. */
function ActionTileSkeleton() {
  return (
    <CardSurface className="flex-row items-start gap-2.5 p-3.5">
      <Skeleton className="size-8 shrink-0 rounded-md" />
      <div className="min-w-0 flex-1 pr-5">
        <TextLine width="w-28" />
        <TextLine width="w-44" />
      </div>
    </CardSurface>
  )
}

/** `QrActionsPanel` — the heading row, its one line of copy, and the grid. */
function ActionsPanelSkeleton({ actions = 2 }: { actions?: number }) {
  return (
    <>
      <div className="border-border flex items-center gap-2 border-b pb-2.5">
        <TextLine width="w-44" line="h-8" bar="h-3" className="flex-1" />
        <Skeleton className="h-8 w-40 rounded-md" />
      </div>

      <TextLine width="w-[460px] max-w-full" />

      {/*
       * The same auto-fill track as the real grid, not a breakpoint grid: the
       * pane is resizable, so column count follows its width rather than the
       * viewport's, and a `sm:grid-cols-2` stand-in reflows the moment the real
       * tiles land.
       */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(248px,1fr))] gap-3">
        {Array.from({ length: actions }, (_, i) => (
          <ActionTileSkeleton key={i} />
        ))}
        {/* "Point nowhere" is chrome rather than data — it is in the grid
            whatever comes back, so its dashed frame is drawn rather than
            guessed at. */}
        <div
          className={cn(
            'border-card-border flex items-center justify-center gap-2 p-3.5',
            'rounded-[var(--radius-card)] border-[length:var(--radius-card-border)]',
            'border-dashed',
          )}
        >
          <Skeleton className="size-4 shrink-0 rounded-full" />
          <Skeleton className="h-2.5 w-24" />
        </div>
      </div>
    </>
  )
}

/** The 1px separator plus its grip, as `ResizableHandle withHandle` draws it. */
function HandleSkeleton() {
  return (
    <div className="bg-border relative flex w-px items-center justify-center">
      <div className="bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border">
        <GripVerticalIcon className="size-2.5" />
      </div>
    </div>
  )
}

/**
 * The two-pane control panel, at the 38/62 split `QrControlPanel` opens on.
 *
 * Plain flex rather than the real `ResizablePanelGroup` — the panels only
 * matter once someone can drag them, and mounting the library to draw a static
 * split would ship it into the fallback. The separator and its grip are drawn
 * to match, since that is the part you would notice moving.
 */
export function ControlPanelSkeleton() {
  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex w-[38%] min-w-0 flex-col">
        <div className="border-border shrink-0 border-b px-6 py-4">
          <QrSummarySkeleton />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
          <ScanSummarySkeleton />
        </div>
      </div>

      <HandleSkeleton />

      <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
        <ActionsPanelSkeleton />
      </div>
    </div>
  )
}

/** One schedule in the left-hand list — icon, name, repeat, enable switch. */
function ScheduleCardSkeleton() {
  return (
    <CardSurface className="flex-row items-center gap-3 p-[var(--radius-card-inset)]">
      <Skeleton className="size-9 shrink-0 rounded-[var(--radius-card-inner)]" />
      <div className="min-w-0 flex-1">
        <TextLine width="w-28" />
        <TextLine width="w-40" />
      </div>
      <Skeleton className="h-[1.15rem] w-8 shrink-0 rounded-full" />
    </CardSurface>
  )
}

/**
 * The Schedules tab: the schedule list, then the selected one's week.
 *
 * A different screen from the control panel, and it used to borrow the control
 * panel's fallback — the `[id]` segment's `loading.tsx` covers this child route
 * too unless the route brings its own.
 *
 * One band row, not seven: `ScheduleDialog` creates daily schedules by default
 * and `ScheduleTimeline` draws daily as a single row. A weekly schedule opens
 * out to seven, which is a growth rather than a collapse — the safer way to be
 * wrong about a count nobody can know yet.
 */
export function SchedulesSkeleton({
  schedules = 2,
  changes = 3,
}: {
  schedules?: number
  changes?: number
}) {
  const list = (
    <>
      {/* Two lines: the intro carries the code's name and wraps in this pane. */}
      <div className="pb-1">
        <TextLine width="w-full" />
        <TextLine width="w-2/3" />
      </div>
      {Array.from({ length: schedules }, (_, i) => (
        <ScheduleCardSkeleton key={i} />
      ))}
    </>
  )

  return (
    <>
      {/* Below `lg` the real screen is one column, list first. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:hidden">
        <div className="flex shrink-0 items-center gap-2 px-6 pt-4 pb-1">
          <TextLine width="w-24" line="h-8" bar="h-3" className="flex-1" />
          <Skeleton className="h-8 w-32 rounded-md" />
        </div>
        <div className="flex flex-col gap-2 px-6 pb-4">{list}</div>
      </div>

      {/* Desktop: the 32/68 split `SchedulesScreen` opens on. */}
      <div className="hidden min-h-0 flex-1 lg:flex">
        <div className="flex w-[32%] min-w-0 flex-col">
          <div className="border-border flex shrink-0 items-center gap-2 border-b px-6 py-3">
            <TextLine width="w-24" line="h-8" bar="h-3" className="flex-1" />
            <Skeleton className="h-8 w-32 rounded-md" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 py-4">
            {list}
          </div>
        </div>

        <HandleSkeleton />

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-border flex shrink-0 items-center gap-2 border-b px-6 py-3">
            <div className="min-w-0 flex-1">
              <TextLine width="w-40" line="h-[22px]" bar="h-3" />
              <TextLine width="w-52" />
            </div>
            <Skeleton className="h-8 w-44 rounded-md" />
            <Skeleton className="size-8 rounded-lg" />
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-4">
            {/* The week at a glance */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <TextLine width="w-44" line="h-[22px]" bar="h-3" />
                <TextLine width="w-full max-w-[520px]" />
              </div>
              {/* The band, at the real geometry. The hour labels above it and
                  the spans inside it are left out on purpose: they are 10px
                  type and 2px ticks, and standing in for them is a lot of
                  fidget for marks nobody reads in the half-second this is up. */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-end gap-2 pl-14">
                  <div className="h-4 flex-1" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex w-12 shrink-0 justify-end">
                    <Skeleton className="h-2.5 w-8" />
                  </div>
                  <Skeleton className="h-10 flex-1 rounded-[var(--radius-band)]" />
                </div>
              </div>
            </div>

            {/* Scheduled changes — one bar per row rather than the real four
                columns, for the same reason. The row height is what has to be
                right; what is inside it is not worth drawing. */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <TextLine width="w-40" line="h-[22px]" bar="h-3" />
                <TextLine width="w-full max-w-[540px]" />
              </div>
              <div className="border-border divide-border divide-y overflow-hidden rounded-xl border">
                {Array.from({ length: changes }, (_, i) => (
                  <div key={i} className="flex h-[52px] items-center gap-3 px-3.5">
                    <Skeleton className="h-2.5 w-12 shrink-0" />
                    <Skeleton className="h-2.5 w-48 max-w-[40%]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/** The org dashboard — KPI row, the big trend card, then the 2-up grid. */
export function DashboardSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 md:px-6">
      <div className="flex h-8 items-center">
        <Skeleton className="h-8 w-56 rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTileSkeleton delta />
        <StatTileSkeleton delta />
        <StatTileSkeleton />
        <StatTileSkeleton />
      </div>
      <PanelCardSkeleton titleWidth="w-32">
        <Skeleton className="h-[220px] w-full rounded-lg" />
      </PanelCardSkeleton>
      <div className="grid gap-3 lg:grid-cols-2">
        {['w-28', 'w-36'].map((width) => (
          <PanelCardSkeleton key={width} titleWidth={width}>
            <div className="flex flex-col gap-2.5">
              {['w-[86%]', 'w-[61%]', 'w-[43%]', 'w-[28%]'].map((barWidth, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex h-[18px] items-center gap-2">
                    <Skeleton className="h-2.5 w-24" />
                    <Skeleton className="ml-auto h-2.5 w-8" />
                  </div>
                  <div className="bg-muted h-1.5 w-full overflow-hidden rounded-r-[4px]">
                    <Skeleton className={cn('h-full rounded-r-[4px]', barWidth)} />
                  </div>
                </div>
              ))}
            </div>
          </PanelCardSkeleton>
        ))}
      </div>
    </div>
  )
}

/** The analytics tab — the same stack as the control panel's left pane. */
export function AnalyticsSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
      <ScanSummarySkeleton />
    </div>
  )
}

/*
 * There is no URL-sniffing skeleton here any more, and that is worth a note
 * because the code that used to be here looked deliberate.
 *
 * Under the App Router a `loading.tsx` did not only cover the screen it was
 * written for: a segment whose LAYOUT was still rendering had no boundary of
 * its own on screen yet, so the nearest parent's stood in for the whole
 * subtree. Opening a QR code drew the QR browser's cards, and every per-code
 * tab drew the control panel's panes. The fix was to read `usePathname()` in
 * the fallback and dispatch to the right shape — two exported components and
 * two path-matching regexes whose only job was to re-derive which route was
 * being navigated to.
 *
 * Pending components are per-route, so the router already knows. Each route in
 * `src/routes/` names its own `pendingComponent` from the pieces above, and
 * nothing has to guess.
 */

export function OrgShellSkeleton() {
  return (
    <div className="bg-background flex h-dvh overflow-hidden">
      {/* Sidebar — fixed width, matching the real one. */}
      <aside className="border-border hidden w-[248px] shrink-0 flex-col gap-4 border-r px-2.5 py-3 md:flex">
        <div className="flex items-center gap-2.5 px-1">
          <Skeleton className="size-8 shrink-0 rounded-lg" />
          <Skeleton className="h-4 w-36" />
        </div>

        <div className="flex items-center gap-2.5 px-1.5">
          <Skeleton className="size-7 shrink-0 rounded-md" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5 px-1">
          <Skeleton className="mb-1 h-2.5 w-20" />
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-lg" />
          ))}
        </div>

        <div className="mt-auto flex items-center gap-2.5 px-1.5">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <Skeleton className="h-3 w-24" />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top bar. */}
        <div className="border-border flex h-14 shrink-0 items-center gap-3 border-b px-4">
          <Skeleton className="h-9 max-w-md flex-1 rounded-lg" />
          <Skeleton className="ml-auto h-8 w-24 rounded-lg" />
          <Skeleton className="size-8 rounded-lg" />
        </div>

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ViewBarSkeleton />
          <BrowserSkeleton />
        </main>
      </div>
    </div>
  )
}
